import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { providerActions } from '../../../../store';
import { ProviderClinicalStatus } from '../../../constants/general';
import { useProviderDetailsState } from './useProviderDetailsState';
import { useProviderPromoteActions } from './useProviderPromoteActions';

import type { AppDispatch } from '../../../../store';

export function useProviderClinicalActions() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { provider, loading, credsLoading, clientAssignmentsLoading } = useProviderDetailsState();
  const {
    showPromoteToClinical,
    selectedRoles,
    setSelectedRoles,
    handlePromoteProvider,
    handlePromoteToClinicalClick,
    closePromoteDialog,
  } = useProviderPromoteActions();
  const [selectedClinicalStatus, setSelectedClinicalStatus] = useState('');
  const [selectedClinicalStatusReason, setSelectedClinicalStatusReason] = useState('');

  const clinicalProvider = provider?.clinical_status === ProviderClinicalStatus.CLINICAL;
  const isLoading = loading || credsLoading || clientAssignmentsLoading;

  const clearClinicalSelections = () => {
    setSelectedClinicalStatus('');
    setSelectedClinicalStatusReason('');
  };

  const handleUpdateClinicalStatus = async () => {
    if (!provider) return;
    closePromoteDialog();
    const res = await dispatch(
      providerActions.updateProviderClinicalStatus({
        providerId: provider?.id,
        data: {
          status: selectedClinicalStatus,
          reason: selectedClinicalStatusReason ? selectedClinicalStatusReason : null,
        },
        getAccessTokenSilently,
      }),
    );
    if (!res.error) {
      clearClinicalSelections();
      dispatch(providerActions.updateProviderLists(res.payload));
    }
  };

  const onPromoteProvider = () => {
    handlePromoteProvider(clearClinicalSelections);
  };

  const onPromoteToClinicalClick = () => {
    handlePromoteToClinicalClick(clearClinicalSelections);
  };

  return {
    provider,
    clinicalProvider,
    isLoading,
    selectedClinicalStatus,
    selectedClinicalStatusReason,
    showPromoteToClinical,
    selectedRoles,
    setSelectedClinicalStatus,
    setSelectedClinicalStatusReason,
    setSelectedRoles,
    handleUpdateClinicalStatus,
    handlePromoteToClinicalClick: onPromoteToClinicalClick,
    handlePromoteProvider: onPromoteProvider,
    closePromoteDialog,
  };
}
